import { useState, useEffect } from "react";
import { ArrowLeft, User, Mail, MessageSquare, Check, Loader2, Sparkles, TrendingUp } from "lucide-react";
import { apiPost } from "../lib/api";

export default function DemoPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiPost("/api/demo", {
        name,
        email,
        whatsapp,
        message,
      });
      setSubmitted(true);
    } catch {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-dark-900/95 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/20"
            : "bg-transparent"
        }`}
      >
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 text-white font-bold text-lg shadow-lg shadow-accent-500/25">
              Z
            </div>
            <span className="text-xl font-bold text-white">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="nav-link flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Home
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="demo-hero relative overflow-hidden">
        <div className="demo-hero-background" aria-hidden="true">
          <div className="demo-hero-grid" />
          <div className="demo-hero-glow demo-hero-glow-primary" />
          <div className="demo-hero-glow demo-hero-glow-secondary" />
        </div>

        <div className="container relative py-12 md:py-16 lg:py-20">
          <div className="demo-hero-layout">
            <div className="demo-hero-copy">
              <span className="badge mb-6 inline-flex">
                <Sparkles className="h-3 w-3" />
                Demonstração gratuita
              </span>
              <h1 className="max-w-2xl text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
                Veja o ZWeb em ação e transforme sua operação.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-surface-300 md:text-xl">
                Agende uma conversa personalizada com nosso time e descubra como
                centralizar vendas, financeiro e atendimento em um só lugar.
              </p>
              <div className="demo-hero-proof mt-8">
                <span><Check className="h-4 w-4" /> 100% gratuito</span>
                <span><Check className="h-4 w-4" /> Sem compromisso</span>
                <span><Check className="h-4 w-4" /> Resposta em 24h</span>
              </div>
            </div>

            <div className="demo-hero-visual" aria-hidden="true">
              <div className="demo-hero-image-shell">
                <img
                  src="/zweb-demo-hero.png"
                  alt="Profissional analisando indicadores em um notebook"
                  className="demo-hero-generated-image"
                />
                <div className="demo-hero-image-overlay" />
                <div className="demo-hero-floating-card demo-hero-floating-card-top">
                  <span className="demo-hero-card-dot" />
                  <div><small>Operação online</small><strong>12 módulos ativos</strong></div>
                </div>
                <div className="demo-hero-floating-card demo-hero-floating-card-bottom">
                  <TrendingUp className="h-4 w-4 text-accent-300" />
                  <div><small>Performance</small><strong>+40% produtividade</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#0B1120"/>
          </svg>
        </div>
      </section>

      {/* Form Section */}
      <main className="section bg-dark-900">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <div className="card overflow-hidden">
              {submitted ? (
                <div className="p-8 text-center md:p-12">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-accent-500/10 glow-box">
                    <Check className="h-10 w-10 text-accent-400" />
                  </div>
                  <h2 className="mt-6 text-3xl font-bold text-white">Solicitação Enviada!</h2>
                  <p className="mt-4 text-surface-300">
                    Entraremos em contato em até 24 horas para confirmar sua demonstração.
                  </p>
                  <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
                    <a
                      href="/"
                      className="btn-primary"
                    >
                      Voltar para Home
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-6 md:p-8">
                  <div className="mb-8">
                    <h2 className="text-2xl font-bold text-white">Preencha seus dados</h2>
                    <p className="mt-2 text-surface-400">Campos com * são obrigatórios</p>
                  </div>

                  <div className="space-y-5">
                    {/* Name */}
                    <div>
                      <label className="mb-2 flex items-center gap-2 text-sm font-medium text-surface-200">
                        <User className="h-4 w-4 text-accent-400" />
                        Nome completo *
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="input"
                        placeholder="Seu nome completo"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="mb-2 flex items-center gap-2 text-sm font-medium text-surface-200">
                        <Mail className="h-4 w-4 text-accent-400" />
                        E-mail *
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input"
                        placeholder="seu@email.com"
                        required
                      />
                    </div>

                    {/* WhatsApp */}
                    <div>
                      <label className="mb-2 flex items-center gap-2 text-sm font-medium text-surface-200">
                        <MessageSquare className="h-4 w-4 text-accent-400" />
                        WhatsApp *
                      </label>
                      <input
                        type="tel"
                        value={whatsapp}
                        onChange={(e) => setWhatsapp(e.target.value)}
                        className="input"
                        placeholder="5511999999999"
                        required
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="mb-2 flex items-center gap-2 text-sm font-medium text-surface-200">
                        <MessageSquare className="h-4 w-4 text-accent-400" />
                        Mensagem
                      </label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        className="input resize-none"
                        placeholder="Conte-nos mais sobre suas necessidades..."
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-8">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          Solicitar Demonstração
                          <ArrowLeft className="h-5 w-5 rotate-180" />
                        </>
                      )}
                    </button>
                  </div>

                  {/* Trust */}
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-surface-500">
                    <div className="flex items-center gap-1">
                      <Check className="h-3 w-3 text-accent-400" />
                      <span>100% gratuito</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="h-3 w-3 text-accent-400" />
                      <span>Sem compromisso</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="h-3 w-3 text-accent-400" />
                      <span>Resposta em 24h</span>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-dark-900">
        <div className="container py-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-accent-500 to-accent-600 text-white font-bold text-sm">
                Z
              </div>
              <span className="text-lg font-bold text-white">ZWeb</span>
            </div>
            <p className="text-sm text-surface-500">
              © {new Date().getFullYear()} ZWeb. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
